import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Gavel, Award, FileUp, FileClock, UserPlus, Clock } from "lucide-react";

interface Props {
  userId: string;
}

interface ActivityItem {
  id: string;
  ts: string;
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Section 6 — Recent activity feed.
 * Unions bids, awards, docs, quote expirations, and network joins; takes most recent 7.
 */
export function RecentActivityFeed({ userId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["shipper-recent-activity", userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 14 * 86_400_000).toISOString();

      const { data: loads } = await supabase
        .from("direct_loads")
        .select(
          "id, origin_city, origin_state, destination_city, destination_state, awarded_at, awarded_bid_id",
        )
        .eq("created_by", userId);
      const loadList = loads || [];
      const loadIds = loadList.map((l: any) => l.id);
      const lane = (l: any) =>
        `${l.origin_city || "—"} → ${l.destination_city || "—"}`;
      const loadById = new Map(loadList.map((l: any) => [l.id, l]));

      const items: ActivityItem[] = [];

      const { data: bids } = loadIds.length
        ? await supabase
            .from("direct_bids")
            .select("id, direct_load_id, bid_amount, submitted_at, broker_id, status")
            .in("direct_load_id", loadIds)
            .gte("submitted_at", since)
            .order("submitted_at", { ascending: false })
            .limit(20)
        : { data: [] as any[] };
      const brokerIds = Array.from(new Set((bids || []).map((b: any) => b.broker_id).filter(Boolean)));
      const { data: brokers } = brokerIds.length
        ? await supabase
            .from("profiles")
            .select("id, first_name, last_name, email")
            .in("id", brokerIds)
        : { data: [] as any[] };
      const brokerName = new Map(
        (brokers || []).map((b: any) => [
          b.id,
          [b.first_name, b.last_name].filter(Boolean).join(" ") || b.email || "Broker",
        ]),
      );

      (bids || []).forEach((b: any) => {
        const l: any = loadById.get(b.direct_load_id);
        if (!l) return;
        items.push({
          id: `bid-${b.id}`,
          ts: b.submitted_at,
          icon: Gavel,
          text: `${brokerName.get(b.broker_id) || "Broker"} bid $${Number(b.bid_amount).toLocaleString()} on ${lane(l)}`,
        });
      });

      loadList
        .filter((l: any) => l.awarded_at && l.awarded_at >= since)
        .forEach((l: any) => {
          items.push({
            id: `award-${l.id}`,
            ts: l.awarded_at,
            icon: Award,
            text: `Awarded ${lane(l)}`,
          });
        });

      // Doc uploads on shipper dispatches.
      const { data: dispatches } = await supabase
        .from("direct_dispatches")
        .select("id, direct_load_id")
        .eq("shipper_id", userId);
      const dIds = (dispatches || []).map((d: any) => d.id);
      const dToLoad = new Map((dispatches || []).map((d: any) => [d.id, d.direct_load_id]));
      const { data: docs } = dIds.length
        ? await supabase
            .from("direct_dispatch_documents")
            .select("id, dispatch_id, doc_type, created_at")
            .in("dispatch_id", dIds)
            .gte("created_at", since)
            .order("created_at", { ascending: false })
            .limit(10)
        : { data: [] as any[] };
      (docs || []).forEach((d: any) => {
        const l: any = loadById.get(dToLoad.get(d.dispatch_id));
        items.push({
          id: `doc-${d.id}`,
          ts: d.created_at,
          icon: FileUp,
          text: `${d.doc_type || "Document"} uploaded${l ? ` for ${lane(l)}` : ""}`,
        });
      });

      // Quote request closures.
      const { data: quotes } = await supabase
        .from("direct_quote_requests")
        .select("id, origin_city, destination_city, status, closed_at, updated_at")
        .eq("created_by", userId)
        .gte("updated_at", since)
        .order("updated_at", { ascending: false })
        .limit(10);
      (quotes || []).forEach((q: any) => {
        if (q.status === "open") return;
        items.push({
          id: `quote-${q.id}`,
          ts: q.closed_at || q.updated_at,
          icon: FileClock,
          text: `Quote request ${q.origin_city} → ${q.destination_city} ${q.status}`,
        });
      });

      // Network joins.
      const { data: networks } = await supabase
        .from("shipper_private_networks")
        .select("id")
        .eq("shipper_user_id", userId);
      const netIds = (networks || []).map((n: any) => n.id);
      const { data: members } = netIds.length
        ? await supabase
            .from("shipper_network_brokers")
            .select("id, broker_user_id, added_at")
            .in("network_id", netIds)
            .gte("added_at", since)
            .order("added_at", { ascending: false })
            .limit(10)
        : { data: [] as any[] };
      const memberBrokerIds = (members || []).map((m: any) => m.broker_user_id);
      const { data: memberProfiles } = memberBrokerIds.length
        ? await supabase
            .from("profiles")
            .select("id, first_name, last_name, email")
            .in("id", memberBrokerIds)
        : { data: [] as any[] };
      const memberName = new Map(
        (memberProfiles || []).map((p: any) => [
          p.id,
          [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "Broker",
        ]),
      );
      (members || []).forEach((m: any) => {
        items.push({
          id: `join-${m.id}`,
          ts: m.added_at,
          icon: UserPlus,
          text: `${memberName.get(m.broker_user_id) || "Broker"} joined your network`,
        });
      });

      return items.sort((a, b) => (a.ts < b.ts ? 1 : -1)).slice(0, 7);
    },
  });

  return (
    <section className="rounded-lg border bg-card p-4">
      <h3 className="mb-3 text-sm font-bold text-foreground">Recent Activity</h3>
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-9 rounded bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : !data || data.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
          No recent activity yet. Post your first load to get started.
        </div>
      ) : (
        <ul className="divide-y">
          {data.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.id} className="flex items-center gap-3 py-2 text-xs">
                <Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-foreground">{it.text}</span>
                <span className="flex flex-shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {timeAgo(it.ts)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
